<template>
  <page-template :is-loading="isLoading" :data-exists="securityPolicies !== null">
    <template #pageHeadline>
      {{ $t('securityPolicy.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="securityPolicies"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="security-policies-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewSecurityPolicyPath()"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateSecurityPolicy"
        @delete-row-button-clicked="openDeleteSecurityPolicyModal"
        @download-data="downloadDoc"
      >
      </rbz-table>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="loadSecurityPolicies"
      />
      <rbz-modal-dialog
        :show="showDeleteSecurityPolicyModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteSecurityPolicy"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteSecurityPolicyModal = false"
        @closeModal="showDeleteSecurityPolicyModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedSecurityPolicy.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { DocumentName, SecurityPolicy, SecurityPolicyEntryMatch } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import _ from 'lodash';
import GitHistory from '@/components/GitHistory.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'SecurityPoliciesList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    GitHistory,
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('securityPolicy.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getSecurityPolicyPath(item);
          },
        },
        {
          id: 'content-filter-profiles',
          title: this.$t('securityPolicy.listPage.contentFilterProfiles'),
          fieldNames: ['map'],
          displayFunction: (item: SecurityPolicy) => {
            const active =
              _.filter(item.map, (mapEntry: SecurityPolicyEntryMatch) => {
                return mapEntry['content_filter_profile_active'];
              }).length || 0;
            const total = item.map?.length || 0;
            return this.$t('securityPolicy.listPage.activeCounter', { active, total });
          },
          isSearchable: true,
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'acl-profiles',
          title: this.$t('securityPolicy.listPage.aclProfiles'),
          fieldNames: ['map'],
          displayFunction: (item: SecurityPolicy) => {
            const active =
              _.filter(item.map, (mapEntry: SecurityPolicyEntryMatch) => {
                return mapEntry['acl_profile_active'];
              }).length || 0;
            const total = item.map?.length || 0;
            return this.$t('securityPolicy.listPage.activeCounter', { active, total });
          },
          isSearchable: true,
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'rate-limit-rules',
          title: this.$t('securityPolicy.listPage.rateLimitRules'),
          fieldNames: ['map'],
          displayFunction: (item: SecurityPolicy) => {
            return (
              _.sumBy(item.map, (mapEntry: SecurityPolicyEntryMatch) => {
                return mapEntry['rate_limit_rules'].length;
              }) || 0
            );
          },
          isSearchable: true,
          isNumber: true,
          classes: 'width-180px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'edge-functions',
          title: this.$t('securityPolicy.listPage.edgeFunctions'),
          fieldNames: ['map'],
          displayFunction: (item: SecurityPolicy) => {
            return (
              _.sumBy(item.map, (mapEntry) => {
                return mapEntry['edge_functions'].length;
              }) || 0
            );
          },
          isSearchable: true,
          isNumber: true,
          classes: 'width-180px',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getSecurityPolicyPath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      securityPolicies: null as SecurityPolicy[],
      pageLoadingCounter: null,

      showDeleteSecurityPolicyModal: false,
      selectedSecurityPolicy: null,

      referencedIds: [],
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if (
          (this.$route.name as string).includes('Security Policies List') &&
          val &&
          val !== oldVal
        ) {
          this.setPageLoadingStatus(true);
          await this.loadSecurityPolicies();
          await this.loadReferencedIds();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('securityPolicy.plural'),
        singular: this.$t('securityPolicy.singular'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/security-policies/versions`;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    async loadSecurityPolicies() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/security-policies`;
      const fieldNames = _.flatMap(this.columns, 'fieldNames');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
      });
      this.securityPolicies = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/server-groups`,
      });
      const serverGroups = response?.data?.items || [];
      const referencedSecurityPolicies: string[] = [];
      _.forEach(serverGroups, (serverGroup) => {
        referencedSecurityPolicies.push(serverGroup['security_policy']);
      });
      this.referencedIds = _.uniq(referencedSecurityPolicies);
      this.setPageLoadingStatus(false);
    },

    isDeleteDisabled(securityPolicy: SecurityPolicy) {
      return (
        this.isSecurityPolicyProtected(securityPolicy) ||
        this.isSecurityPolicyReferenced(securityPolicy)
      );
    },

    isSecurityPolicyProtected(securityPolicy: SecurityPolicy): boolean {
      return securityPolicy.id.startsWith('__');
    },

    isSecurityPolicyReferenced(securityPolicy: SecurityPolicy): boolean {
      return this.referencedIds.includes(securityPolicy.id);
    },

    deleteTooltip(securityPolicy: SecurityPolicy): string {
      if (this.isSecurityPolicyProtected(securityPolicy)) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.isSecurityPolicyReferenced(securityPolicy)) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    getSecurityPolicyPath(securityPolicy: SecurityPolicy) {
      return this.getEditPath('security-policies', securityPolicy.id);
    },

    editSecurityPolicy(securityPolicy: SecurityPolicy) {
      this.navigateTo(this.getSecurityPolicyPath(securityPolicy));
    },

    async duplicateSecurityPolicy(securityPolicy: SecurityPolicy) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies/${securityPolicy.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalSecurityPolicy = getOriginalResponse.data;
      const securityPolicyToAdd = _.cloneDeep(originalSecurityPolicy);
      securityPolicyToAdd.id = DatasetsUtils.generateUUID2();
      securityPolicyToAdd.name = Utils.generateUniqueEntityName(
        securityPolicyToAdd.name,
        _.map(this.securityPolicies, 'name'),
        true
      );
      const url = `conf/${this.selectedBranch}/security-policies/${securityPolicyToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: securityPolicyToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editSecurityPolicy(securityPolicyToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteSecurityPolicy() {
      this.setPageLoadingStatus(true);
      const securityPolicyId = this.selectedSecurityPolicy.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/security-policies/${securityPolicyId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.securityPolicies = _.filter(this.securityPolicies, (securityPolicy) => {
          return securityPolicy.id !== securityPolicyId;
        });
      }
      this.showDeleteSecurityPolicyModal = false;
      this.setPageLoadingStatus(false);
    },

    downloadDoc() {
      Utils.downloadFile('security-policies', 'json', this.securityPolicies);
    },

    openDeleteSecurityPolicyModal(securityPolicy: SecurityPolicy) {
      this.showDeleteSecurityPolicyModal = true;
      this.selectedSecurityPolicy = securityPolicy;
    },

    getNewSecurityPolicyPath() {
      return this.getNewPath('security-policies');
    },
  },
});
</script>
